'use client';

import { ChangeEvent, FocusEvent, KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useUnit } from 'effector-react';
import classNames from 'classnames';
import { useToast } from '@nx-frontend/shared/ui/toast';
import { upsertWorkspaceNote } from '@nx-frontend/shared/api/webnotes';
import { $note, updateCurrentNoteEvent } from '@nx-frontend/shared/store/note';

export const NoteTitle = () => {
  const t = useTranslations('webnotes');
  const [currentNote, updateCurrentNote] = useUnit([$note, updateCurrentNoteEvent]);
  const currentTitle = currentNote.title.trim();
  const [isEdit, setIsEdit] = useState(false);
  const [currentValue, setCurrentValue] = useState(currentTitle ?? '');
  const [currentId, setCurrentId] = useState(currentNote.globalId);

  const inputRef = useRef<HTMLInputElement>(null);

  const { toast } = useToast();

  useEffect(() => {
    if (currentId !== currentNote.globalId) {
      setCurrentId(currentNote.globalId);
      setCurrentValue(currentTitle);
      setIsEdit(false);
    }
  }, [currentNote.globalId, currentId, currentTitle]);

  const applyNewTitle = useCallback(
    async (value: string) => {
      const updatedNote = await upsertWorkspaceNote({
        workspaceId: currentNote.workspaceId,
        noteId: currentNote.globalId,
        note: {
          title: value.trim(),
        },
      });
      if (updatedNote) {
        updateCurrentNote(updatedNote);
      } else {
        toast({ title: 'Failed to rename page, try again' });
        setCurrentValue(currentTitle);
      }
      setIsEdit(false);
    },
    [currentNote.globalId, currentNote.workspaceId, updateCurrentNote, toast, currentTitle]
  );

  const onEditClick = useCallback(() => {
    setIsEdit(true);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.select();
      }
    }, 0);
  }, []);

  const onTitleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setCurrentValue(event.target.value);
  }, []);

  const onTitleBlur = (event: FocusEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value && value !== currentTitle) {
      applyNewTitle(event.target.value);
    } else {
      setIsEdit(false);
      setCurrentValue(currentTitle);
    }
  };

  const onTitleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      if (inputRef.current) {
        const value = inputRef.current.value;
        if (value && value !== currentTitle) {
          applyNewTitle(inputRef.current.value);
        } else {
          setIsEdit(false);
          setCurrentValue(currentTitle);
        }
      }
    } else if (event.key === 'Escape') {
      setIsEdit(false);
      setCurrentValue(currentTitle);
    }
  };

  const isFullwidth = currentNote && currentNote.isFullwidth;

  return (
    <div data-cy="editor-page-title" className="text-2xl font-bold pl-[30px] pr-[40px] w-full">
      <div
        className={classNames(
          {
            'max-w-1000': !isFullwidth,
          },
          'mx-auto'
        )}
      >
        {!isEdit ? (
          <span className="cursor-pointer" onClick={onEditClick}>
            {currentTitle ?? ''}
          </span>
        ) : (
          <input
            ref={inputRef}
            onChange={onTitleChange}
            className="w-full border-0 outline-none caret-cyan-500"
            type="text"
            placeholder={t('webnotes-new_page')}
            value={currentValue}
            onBlur={onTitleBlur}
            onKeyDown={onTitleKeyDown}
          />
        )}
      </div>
    </div>
  );
};
